/**
 * The live house store.
 *
 * Fed by one websocket carrying every home the user can see. Each piece of
 * state lives under a string key (`e|home|entity`, `m|home`, …) with its own
 * listener set, so `useSyncExternalStore` hooks re-render only the component
 * that reads the key that changed: a temperature diff repaints one tile.
 *
 * Optimistic UI: `call()` can overlay a predicted state on an entity. The
 * overlay is dropped when the server reports any change to that entity after
 * the call, when the call fails (rollback + toast) or after 5s of silence
 * (rollback + "didn't respond" toast).
 */
import type {
  Alert,
  Boost,
  Capability,
  ClientMsg,
  ClimateActionMsg,
  ClimateActionResult,
  HeatingMsg,
  EntityMeta,
  EntityState,
  HistoryData,
  HomeModel,
  HomeSummary,
  LogbookRow,
  PublicUser,
  ResolvedAccess,
  ServerMsg,
  StatisticsData,
} from '@home/shared';
import type { PackedMsg, TabletCommand } from '@home/shared';
import { unpackAccess, unpackModel, unpackStates } from '@home/shared/wire';
import { HomeSocket, type SocketStatus } from './ws.js';
import { loadCached, removeHome, saveHome, saveSession } from './snapshot-cache.js';
import { toast } from './toast.js';

export interface HomeData {
  model: HomeModel;
  access: ResolvedAccess;
  states: Map<string, EntityState>;
  boosts: Boost[];
}

type Listener = () => void;

export class CallError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
  }
}

interface Overlay {
  state: EntityState | undefined;
  sentAt: number;
  timer: number;
}

interface PendingReq {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
  timer: number;
}

export interface CallOptions {
  /** Predicts the new state for each target entity, shown until the server confirms. */
  optimistic?: (current: EntityState, entityId: string) => EntityState;
  /** Human label for toasts, e.g. "Kitchen lights". Defaults to the entity name. */
  label?: string;
  /** Don't show an error toast (caller handles it). */
  silent?: boolean;
}

const CONFIRM_TIMEOUT_MS = 5000;
const REQUEST_TIMEOUT_MS = 20_000;

const ek = (home: string, id: string): string => `e|${home}|${id}`;
const pk = (home: string, id: string): string => `p|${home}|${id}`;

class Store {
  private listeners = new Map<string, Set<Listener>>();
  private versions = new Map<string, number>();

  homes = new Map<string, HomeData>();
  /** Server-side alerts per home (AlertsMsg), keyed `al|home` for subscribers. */
  serverAlerts = new Map<string, Alert[]>();
  /** House heating mode + overrides per home (HeatingMsg), keyed `h|home` for subscribers. */
  heating = new Map<string, HeatingMsg>();
  /** Maintenance report version per home (MaintenanceMsg), keyed `mt|home`: the dashboard refetches on change. */
  maintenance = new Map<string, string>();
  homeList: HomeSummary[] = [];
  user: PublicUser | null = null;
  status: SocketStatus = 'idle';
  /** True once welcome + every snapshot has arrived at least once (or a cached copy was painted). */
  ready = false;
  /** Showing the IndexedDB copy from a previous session until live snapshots arrive. */
  stale = false;
  serverOffset = 0;
  build = '';

  private overlays = new Map<string, Overlay>();
  private pending = new Map<string, number>();
  private requests = new Map<number, PendingReq>();
  private seq = 0;
  private awaitingSnapshots = new Set<string>();

  private socket = new HomeSocket({
    onMessage: (m) => this.onMessage(m),
    onStatus: (s) => {
      this.status = s;
      if (s !== 'live') this.failRequests('offline');
      this.emit('conn');
    },
    onOpen: () => undefined,
  });

  // ------------------------------------------------------------ plumbing

  subscribe = (key: string, fn: Listener): (() => void) => {
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    set.add(fn);
    return () => {
      set.delete(fn);
      if (set.size === 0) this.listeners.delete(key);
    };
  };

  version(key: string): number {
    return this.versions.get(key) ?? 0;
  }

  private emit(key: string): void {
    this.versions.set(key, (this.versions.get(key) ?? 0) + 1);
    const set = this.listeners.get(key);
    if (set) for (const fn of [...set]) fn();
  }

  // ------------------------------------------------------------ lifecycle

  private stopTimer: number | undefined;

  private cacheUser: string | null = null;
  private persistTimers = new Map<string, number>();
  private lastPersist = new Map<string, number>();

  /** `userId` (from the REST session) enables the offline snapshot cache. */
  connect(userId?: string): void {
    window.clearTimeout(this.stopTimer);
    if (userId && this.cacheUser !== userId) {
      this.cacheUser = userId;
      if (!this.ready) void this.hydrate(userId);
    }
    this.socket.start();
    window.addEventListener('pagehide', this.persistAll);
    document.addEventListener('visibilitychange', this.onHidden);
  }

  /** Paint the last known state while the socket (re)connects. Live data always wins. */
  private async hydrate(userId: string): Promise<void> {
    const t0 = performance.now();
    const { session, homes } = await loadCached(userId);
    if (this.cacheUser !== userId || this.user || this.homes.size || !session || !homes.length) return;
    this.user = session.user;
    this.homeList = session.homes.map((h) => ({ ...h }));
    for (const c of homes) {
      if (!session.homes.some((h) => h.id === c.home)) continue;
      this.homes.set(c.home, { model: c.model, access: c.access, states: new Map(Object.entries(c.states)), boosts: c.boosts });
      this.emitHome(c.home);
    }
    this.stale = true;
    this.emit('stale');
    this.emit('homes');
    this.emit('user');
    this.markReady();
    if (import.meta.env.DEV) console.debug(`[store] painted cached state in ${Math.round(performance.now() - t0)}ms`);
  }

  private onHidden = (): void => {
    if (document.visibilityState === 'hidden') this.persistAll();
  };

  private persistAll = (): void => {
    for (const home of this.homes.keys()) this.persist(home);
  };

  private persist(home: string): void {
    const h = this.homes.get(home);
    if (!h || !this.cacheUser || this.stale || !this.user || this.user.id !== this.cacheUser) return;
    window.clearTimeout(this.persistTimers.get(home));
    this.persistTimers.delete(home);
    this.lastPersist.set(home, Date.now());
    void saveHome({ userId: this.cacheUser, home, model: h.model, access: h.access, states: Object.fromEntries(h.states), boosts: h.boosts });
  }

  /** At most once a minute while live; immediately when the page hides. */
  private schedulePersist(home: string): void {
    if (this.persistTimers.has(home)) return;
    const wait = Math.max(0, 60_000 - (Date.now() - (this.lastPersist.get(home) ?? 0)));
    this.persistTimers.set(home, window.setTimeout(() => this.persist(home), wait));
  }

  /**
   * Deferred by a tick so an unmount/remount (React StrictMode, fast route
   * swaps) keeps the socket: WebKit stalls a new handshake to the same host
   * while a just-closed CONNECTING socket is still tearing down.
   */
  disconnect(immediate = false): void {
    window.clearTimeout(this.stopTimer);
    const stop = (): void => {
      this.persistAll();
      this.socket.stop();
      window.removeEventListener('pagehide', this.persistAll);
      document.removeEventListener('visibilitychange', this.onHidden);
      for (const t of this.persistTimers.values()) window.clearTimeout(t);
      this.persistTimers.clear();
      this.cacheUser = null;
      this.reset();
    };
    if (immediate) stop();
    else this.stopTimer = window.setTimeout(stop, 50);
  }

  private reset(): void {
    for (const o of this.overlays.values()) window.clearTimeout(o.timer);
    this.overlays.clear();
    this.pending.clear();
    const homes = [...this.homes.keys()];
    this.homes.clear();
    this.homeList = [];
    for (const h of this.serverAlerts.keys()) this.emit(`al|${h}`);
    this.serverAlerts.clear();
    for (const h of this.heating.keys()) this.emit(`h|${h}`);
    this.heating.clear();
    this.user = null;
    this.ready = false;
    this.stale = false;
    this.emit('stale');
    for (const h of homes) this.emitHome(h);
    this.emit('homes');
    this.emit('user');
    this.emit('ready');
  }

  // ------------------------------------------------------------ reads

  home(home: string): HomeData | undefined {
    return this.homes.get(home);
  }

  entity(home: string, id: string): EntityState | undefined {
    const o = this.overlays.get(ek(home, id));
    if (o) return o.state;
    return this.homes.get(home)?.states.get(id);
  }

  meta(home: string, id: string): EntityMeta | undefined {
    return this.homes.get(home)?.model.entities[id];
  }

  isPending(home: string, id: string): boolean {
    return (this.pending.get(pk(home, id)) ?? 0) > 0;
  }

  caps(home: string, id: string): Capability[] | undefined {
    return this.homes.get(home)?.access.entities[id];
  }

  now(): number {
    return Date.now() + this.serverOffset;
  }

  // ------------------------------------------------------------ messages

  /** Expands the compact wire form (shared/wire.ts) into protocol.ts messages. */
  private unpack(msg: ServerMsg | PackedMsg): ServerMsg {
    if (!('z' in msg)) return msg;
    switch (msg.t) {
      case 'snapshot': {
        const model = unpackModel(msg.model);
        return { t: 'snapshot', home: msg.home, model, access: unpackAccess(msg.access, Object.keys(model.entities)), states: unpackStates(msg.states) };
      }
      case 'diff':
        return { t: 'diff', home: msg.home, changed: unpackStates(msg.changed), ...(msg.removed ? { removed: msg.removed } : {}) };
      case 'model':
        return { t: 'model', home: msg.home, model: unpackModel(msg.model) };
      case 'access': {
        const ids = Object.keys(this.homes.get(msg.home)?.model.entities ?? {});
        return { t: 'access', home: msg.home, access: unpackAccess(msg.access, ids) };
      }
    }
  }

  private onMessage(raw: ServerMsg | PackedMsg): void {
    const msg = this.unpack(raw);
    // Feature modules that own their messages (screens/toilets: `{t:'toilets'}`) see every message first.
    for (const fn of [...this.rawListeners]) {
      try { fn(msg); } catch (err) { console.error('[ws] message listener failed', err); }
    }
    switch (msg.t) {
      case 'welcome': {
        this.user = msg.user;
        this.homeList = msg.homes;
        this.build = msg.build;
        this.serverOffset = msg.serverTime * (msg.serverTime < 1e12 ? 1000 : 1) - Date.now();
        // Drop homes the user can no longer see.
        for (const h of [...this.homes.keys()]) {
          if (!msg.homes.some((x) => x.id === h)) {
            this.homes.delete(h);
            this.emitHome(h);
            if (this.cacheUser) void removeHome(this.cacheUser, h);
          }
        }
        if (this.cacheUser === msg.user.id && !msg.user.mustChangePassword) void saveSession(msg.user, msg.homes);
        this.awaitingSnapshots = new Set(msg.homes.map((h) => h.id));
        if (msg.homes.length === 0) this.markReady();
        this.emit('homes');
        this.emit('user');
        break;
      }
      case 'snapshot': {
        const prev = this.homes.get(msg.home);
        const states = new Map(Object.entries(msg.states));
        this.homes.set(msg.home, { model: msg.model, access: msg.access, states, boosts: msg.model.boosts ?? [] });
        // Every entity that existed before or exists now may have changed.
        const ids = new Set<string>(states.keys());
        if (prev) for (const id of prev.states.keys()) ids.add(id);
        for (const id of ids) {
          this.settle(msg.home, id);
          this.emit(ek(msg.home, id));
        }
        this.emitHome(msg.home);
        this.awaitingSnapshots.delete(msg.home);
        if (this.awaitingSnapshots.size === 0) {
          this.markReady();
          if (this.stale) {
            this.stale = false;
            this.emit('stale');
          }
          for (const h of this.homes.keys()) this.schedulePersist(h);
        }
        break;
      }
      case 'diff': {
        const h = this.homes.get(msg.home);
        if (!h) return;
        for (const [id, s] of Object.entries(msg.changed)) {
          h.states.set(id, s);
          this.settle(msg.home, id);
          this.emit(ek(msg.home, id));
        }
        for (const id of msg.removed ?? []) {
          h.states.delete(id);
          this.emit(ek(msg.home, id));
        }
        this.schedulePersist(msg.home);
        break;
      }
      case 'model': {
        const h = this.homes.get(msg.home);
        if (!h) return;
        h.model = msg.model;
        this.emit(`m|${msg.home}`);
        break;
      }
      case 'access': {
        const h = this.homes.get(msg.home);
        if (!h) return;
        const before = h.access;
        h.access = msg.access;
        for (const id of [...h.states.keys()]) {
          if (!(id in msg.access.entities)) {
            h.states.delete(id);
            this.emit(ek(msg.home, id));
          }
        }
        if (before.inWindow !== msg.access.inWindow) {
          toast(msg.access.inWindow ? 'Controls are available again' : 'Controls are paused until your next access window', {
            tone: msg.access.inWindow ? 'positive' : 'caution',
          });
        }
        this.emit(`a|${msg.home}`);
        break;
      }
      case 'home_status': {
        this.homeList = this.homeList.map((x) => (x.id === msg.home.id ? msg.home : x));
        const h = this.homes.get(msg.home.id);
        if (h) h.model = { ...h.model, home: msg.home };
        this.emit('homes');
        this.emit(`s|${msg.home.id}`);
        break;
      }
      case 'boosts': {
        const h = this.homes.get(msg.home);
        if (!h) return;
        h.boosts = msg.boosts;
        this.emit(`b|${msg.home}`);
        break;
      }
      case 'result': {
        const r = this.requests.get(msg.id);
        if (!r) return;
        this.requests.delete(msg.id);
        window.clearTimeout(r.timer);
        if (msg.ok) r.resolve(msg.data);
        else r.reject(new CallError(msg.error, msg.code));
        break;
      }
      case 'alerts': {
        // Server-evaluated alert rules (ha/alerts.ts). Full replacement per home.
        this.serverAlerts.set(msg.home, msg.alerts);
        this.emit(`al|${msg.home}`);
        break;
      }
      case 'heating': {
        // House heating mode, per-system heat info and room overrides (server ha/heating.ts).
        this.heating.set(msg.home, msg);
        this.emit(`h|${msg.home}`);
        break;
      }
      case 'maintenance': {
        if (this.maintenance.get(msg.home) !== msg.version) {
          this.maintenance.set(msg.home, msg.version);
          this.emit(`mt|${msg.home}`);
        }
        break;
      }
      case 'tablet': {
        // Wall tablets only (screens/tablet): listeners get each command once, in order.
        for (const fn of [...this.tabletListeners]) {
          try { fn(msg.command); } catch (err) { console.error('[tablet] command handler failed', err); }
        }
        break;
      }
      case 'pong':
        break;
    }
  }

  private rawListeners = new Set<(m: ServerMsg) => void>();
  /** Every server message (unpacked), for feature modules with their own message kinds. */
  onServerMessage(fn: (m: ServerMsg) => void): () => void {
    this.rawListeners.add(fn);
    return () => this.rawListeners.delete(fn);
  }

  private tabletListeners = new Set<(c: TabletCommand) => void>();
  /** Server → tablet commands (`{t:'tablet'}`), for the /t/:tabletId page. */
  onTabletCommand(fn: (c: TabletCommand) => void): () => void {
    this.tabletListeners.add(fn);
    return () => this.tabletListeners.delete(fn);
  }

  private markReady(): void {
    if (this.ready) return;
    this.ready = true;
    this.emit('ready');
  }

  private emitHome(home: string): void {
    this.emit(`m|${home}`);
    this.emit(`a|${home}`);
    this.emit(`b|${home}`);
    this.emit(`s|${home}`);
  }

  /** A server state arrived for this entity: drop any optimistic overlay. */
  private settle(home: string, id: string): void {
    const key = ek(home, id);
    const o = this.overlays.get(key);
    if (!o) return;
    window.clearTimeout(o.timer);
    this.overlays.delete(key);
    this.setPending(home, id, -1);
  }

  private rollback(home: string, id: string): boolean {
    const key = ek(home, id);
    const o = this.overlays.get(key);
    if (!o) return false;
    window.clearTimeout(o.timer);
    this.overlays.delete(key);
    this.setPending(home, id, -1);
    this.emit(key);
    return true;
  }

  private setPending(home: string, id: string, delta: number): void {
    const key = pk(home, id);
    const n = Math.max(0, (this.pending.get(key) ?? 0) + delta);
    if (n === 0) this.pending.delete(key);
    else this.pending.set(key, n);
    this.emit(key);
  }

  private failRequests(reason: string): void {
    for (const [id, r] of this.requests) {
      window.clearTimeout(r.timer);
      r.reject(new CallError(reason === 'offline' ? 'Not connected' : reason, 'offline'));
      this.requests.delete(id);
    }
  }

  // ------------------------------------------------------------ requests

  private request<T>(build: (id: number) => ClientMsg, timeoutMs = REQUEST_TIMEOUT_MS): Promise<T> {
    const id = ++this.seq;
    return new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.requests.delete(id);
        reject(new CallError('The server took too long to answer', 'timeout'));
      }, timeoutMs);
      this.requests.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      if (!this.socket.send(build(id))) {
        window.clearTimeout(timer);
        this.requests.delete(id);
        reject(new CallError("You're offline, so that didn't go through", 'offline'));
      }
    });
  }

  /**
   * Calls an HA service on explicit entities. Resolves with the server's
   * result data; rejects (and toasts, unless silent) on failure.
   */
  async call(
    home: string,
    domain: string,
    service: string,
    entityIds: string[],
    data?: Record<string, unknown>,
    opts: CallOptions = {},
  ): Promise<unknown> {
    const label = opts.label ?? (entityIds.length === 1 ? this.meta(home, entityIds[0]!)?.name : undefined) ?? 'That';
    const sentAt = Date.now();
    const overlaid: string[] = [];
    for (const id of entityIds) {
      this.setPending(home, id, +1);
      if (opts.optimistic) {
        const cur = this.entity(home, id);
        if (cur) {
          const key = ek(home, id);
          const existing = this.overlays.get(key);
          if (existing) {
            // Superseded by this newer call; keep one pending count per overlay.
            window.clearTimeout(existing.timer);
            this.setPending(home, id, -1);
          }
          const timer = window.setTimeout(() => {
            if (this.rollback(home, id)) {
              toast(`${label} didn't respond`, { tone: 'caution' });
            }
          }, CONFIRM_TIMEOUT_MS);
          this.overlays.set(key, { state: opts.optimistic(cur, id), sentAt, timer });
          overlaid.push(id);
          this.emit(key);
          continue;
        }
      }
    }
    const clearNonOverlaid = (): void => {
      for (const id of entityIds) if (!overlaid.includes(id)) this.setPending(home, id, -1);
    };
    try {
      const res = await this.request<unknown>((id) => ({
        t: 'call',
        id,
        home,
        domain,
        service,
        entityIds,
        ...(data ? { data } : {}),
      }));
      clearNonOverlaid();
      return res;
    } catch (e) {
      clearNonOverlaid();
      for (const id of overlaid) this.rollback(home, id);
      const err = e instanceof Error ? e : new Error(String(e));
      if (!opts.silent) toast(friendlyError(err, label), { tone: 'alert' });
      throw err;
    }
  }

  history(home: string, entityIds: string[], start: number, end?: number): Promise<HistoryData> {
    return this.request<HistoryData>((id) => ({ t: 'history', id, home, entityIds, start, ...(end ? { end } : {}) }));
  }

  statistics(
    home: string,
    entityIds: string[],
    start: number,
    period: '5minute' | 'hour' | 'day',
    end?: number,
  ): Promise<StatisticsData> {
    return this.request<StatisticsData>((id) => ({
      t: 'history',
      id,
      home,
      entityIds,
      start,
      statistics: period,
      ...(end ? { end } : {}),
    }));
  }

  logbook(home: string, entityId: string, start: number, end?: number): Promise<LogbookRow[]> {
    return this.request<LogbookRow[]>((id) => ({ t: 'logbook', id, home, entityId, start, ...(end ? { end } : {}) }));
  }

  async boost(home: string, systemId: string, action: 'start' | 'cancel'): Promise<void> {
    try {
      await this.request((id) => ({ t: 'boost', id, home, systemId, action }));
    } catch (e) {
      toast(friendlyError(e instanceof Error ? e : new Error(String(e)), 'Boost'), { tone: 'alert' });
      throw e;
    }
  }

  /**
   * Semantic climate action (server plans the HA calls: Neo standby/preset, Nest eco,
   * house-mode overrides). Toasts on failure unless silent.
   */
  async climate(
    home: string,
    systemId: string,
    action: Pick<ClimateActionMsg, 'action' | 'mode' | 'which' | 'value'>,
    opts: { label?: string; silent?: boolean } = {},
  ): Promise<ClimateActionResult> {
    try {
      return await this.request<ClimateActionResult>((id) => ({ t: 'climate', id, home, systemId, ...action }));
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      if (!opts.silent) toast(friendlyError(err, opts.label ?? 'Heating'), { tone: 'alert' });
      throw err;
    }
  }

  resync(home?: string): void {
    this.socket.send(home ? { t: 'resync', home } : { t: 'resync' });
  }
}

function friendlyError(e: Error, label: string): string {
  const code = e instanceof CallError ? e.code : undefined;
  switch (code) {
    case 'forbidden':
      return `You don't have permission to change ${label}`;
    case 'readonly_window':
      return e.message || 'Controls are paused outside your access window';
    case 'offline':
      return e.message || "You're offline, so that didn't go through";
    case 'timeout':
      return `${label} didn't respond`;
    case 'ha_error':
      return `${label}: ${e.message}`;
    default:
      return e.message || `Couldn't change ${label}`;
  }
}

export const store = new Store();
export type { Store };
